import React, { useEffect, useState, useCallback } from 'react';
import MainLayout from '../components/Layout/MainLayout.js';
import FilterBar from '../components/FilterBar.js';
import BlockedOverlay from '../components/BlockedOverlay.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.jsx';
import { Input } from '../components/ui/input.jsx';
import { Label } from '../components/ui/label.jsx';
import { useFilters } from '../context/FilterContext.js';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ComposedChart, Line, Cell, ReferenceLine } from 'recharts';
import { TrendingUp, Users, DollarSign, BarChart3, Scissors, Check, Layers } from 'lucide-react';
import CompetitivenessIndex from '../components/CompetitivenessIndex.js';
import PdfDownloadButton from '../components/PdfDownloadButton.js';

const fmt = (v) => typeof v === 'number' ? `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}` : '-';
const fmtK = (v) => v >= 1000 ? `R$ ${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `R$ ${v}`;

const COMP_OPTIONS = [
  { key: 'horas_extras', label: 'Horas Extras', short: 'HE' },
  { key: 'adicional_noturno', label: 'Adic. Noturno', short: 'AN' },
  { key: 'adicional_periculosidade', label: 'Adic. Periculosidade', short: 'AP' },
  { key: 'adicional_insalubridade', label: 'Adic. Insalubridade', short: 'AI' },
];

const SalaryDashboard = () => {
  const [data, setData] = useState(null);
  const [familia, setFamilia] = useState('');
  const [nivel, setNivel] = useState('');
  const [view, setView] = useState('table');
  const [linhaCorte, setLinhaCorte] = useState('20');
  const [selectedComps, setSelectedComps] = useState([]);
  const [showCompBreakdown, setShowCompBreakdown] = useState(false);
  const { filters, buildQuery } = useFilters();
  const userRole = localStorage.getItem('userRole') || 'respondent';

  const toggleComp = (key) => {
    setSelectedComps(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const loadData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const qs = buildQuery();
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/salary/analysis`;
      const params = new URLSearchParams(qs);
      if (familia) params.set('familia', familia);
      if (nivel) params.set('nivel', nivel);
      params.set('linha_corte', linhaCorte || '20');
      if (selectedComps.length > 0) params.set('componentes', selectedComps.join(','));
      url += '?' + params.toString();
      const { data: result } = await (await import('axios')).default.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(result);
    } catch (e) {
      if (e.response?.status === 403) setData({ blocked: true, detail: e.response.data.detail, n: 0 });
    }
  }, [filters, familia, nivel, linhaCorte, selectedComps, buildQuery]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!data) return <MainLayout title="Pesquisa Salarial" userRole={userRole}><div className="flex items-center justify-center py-20 text-slate-400">Carregando...</div></MainLayout>;

  const blocked = data.blocked;
  const hasComps = selectedComps.length > 0;
  const compostoGeral = data.composto_geral;
  const compStats = data.component_stats || {};
  const medianGeral = (hasComps && compostoGeral) ? compostoGeral.p50 : (data.salario_base_geral?.p50 || 0);

  const activeLabel = hasComps ? `Base + ${selectedComps.map(k => COMP_OPTIONS.find(c => c.key === k)?.short).join(' + ')}` : 'Salário Base';

  const chartData = (data.by_role || []).map(r => {
    const src = hasComps && r.composto ? r.composto : r.salario_base;
    return {
      name: r.cargo.length > 20 ? r.cargo.slice(0, 20) + '...' : r.cargo,
      fullName: r.cargo,
      P25: src?.p25 || 0, P50: src?.p50 || 0, P75: src?.p75 || 0, P90: src?.p90 || 0,
      Media: src?.media || 0, MediaPond: src?.media_ponderada || 0,
      q1: src?.p25 || 0, iq: (src?.p75 || 0) - (src?.p25 || 0),
      upper: (src?.p90 || 0) - (src?.p75 || 0),
      freq: r.frequencia || 0, n: r.n_empresas,
      myVal: r.minha_empresa ? (hasComps ? r.minha_empresa.valor_composto : r.minha_empresa.salario_base) : 0,
    };
  });

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.[0]) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-3 text-xs max-w-[250px]">
        <p className="font-semibold text-slate-900 mb-1.5">{d.fullName}</p>
        <p className="text-[10px] text-blue-600 font-medium mb-1">{activeLabel}</p>
        <div className="space-y-0.5 text-slate-600">
          <p>Frequência: <strong>{d.freq} ocupantes</strong></p>
          <p>1º Quartil: {fmt(d.P25)}</p>
          <p className="text-blue-600 font-semibold">Mediana: {fmt(d.P50)}</p>
          <p>Média Pond.: {fmt(d.MediaPond)}</p>
          <p>3º Quartil: {fmt(d.P75)}</p>
          {d.myVal > 0 && <p className="border-t pt-1 mt-1 text-slate-900">Sua Empresa: <strong>{fmt(d.myVal)}</strong></p>}
        </div>
      </div>
    );
  };

  const filteredCount = (data.by_role || []).filter(r => r.items_removidos_corte > 0).length;

  return (
    <MainLayout title="Pesquisa Salarial" userRole={userRole}>
      <div className="space-y-6" data-testid="salary-dashboard">
        <FilterBar n={data.n} blocked={blocked} />

        {blocked ? <BlockedOverlay n={data.n} /> : (
          <>
            <CompetitivenessIndex variant="compact" />

            {/* Filtros */}
            <div className="flex gap-3 items-end flex-wrap">
              <Select value={familia || "all"} onValueChange={(v) => setFamilia(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="filter-familia"><SelectValue placeholder="Família de Cargo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas Famílias</SelectItem>
                  {(data.familias_disponiveis || []).map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={nivel || "all"} onValueChange={(v) => setNivel(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="filter-nivel"><SelectValue placeholder="Nível" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos Níveis</SelectItem>
                  {(data.niveis_disponiveis || []).map(n => <SelectItem key={n} value={n}>{n.charAt(0).toUpperCase() + n.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
              <div>
                <Label className="text-[10px] text-slate-500 flex items-center gap-1 mb-0.5"><Scissors className="w-3 h-3" /> Linha de Corte (%)</Label>
                <Input type="number" value={linhaCorte} onChange={e => setLinhaCorte(e.target.value)} className="w-[90px] h-9 text-sm" data-testid="linha-corte-input" />
              </div>
              <div className="flex bg-slate-100 rounded-lg p-0.5 ml-auto">
                {[{ id: 'chart', icon: BarChart3, label: 'Gráfico' }, { id: 'table', icon: DollarSign, label: 'Tabela' }].map(v => (
                  <button key={v.id} onClick={() => setView(v.id)} data-testid={`view-${v.id}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === v.id ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
                    <v.icon className="w-3.5 h-3.5" />{v.label}
                  </button>
                ))}
              </div>
              <PdfDownloadButton
                endpoint="/api/reports/salary/pdf"
                queryString={buildQuery()}
                params={{
                  ...(familia ? { familia } : {}),
                  ...(nivel ? { nivel } : {}),
                  ...(selectedComps.length > 0 ? { componentes: selectedComps.join(',') } : {}),
                }}
                label="PDF Remuneração"
                testId="salary-pdf-download"
              />
            </div>

            {/* Seletor de Componentes de Remuneração */}
            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="py-3 px-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700">
                    <Layers className="w-4 h-4" />
                    <span>Composição Salarial:</span>
                  </div>
                  <div className="flex items-center gap-1 bg-slate-900 text-white px-2.5 py-1 rounded-md text-xs font-medium">
                    <Check className="w-3 h-3" /> Base
                  </div>
                  {COMP_OPTIONS.map(opt => {
                    const isActive = selectedComps.includes(opt.key);
                    const stats = compStats[opt.key];
                    return (
                      <button
                        key={opt.key}
                        onClick={() => toggleComp(opt.key)}
                        data-testid={`comp-toggle-${opt.key}`}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all border ${
                          isActive ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'
                        }`}
                      >
                        {isActive && <Check className="w-3 h-3" />}
                        <span>{opt.label}</span>
                        {stats && <span className={`text-[9px] ml-1 ${isActive ? 'opacity-70' : 'text-slate-400'}`}>({stats.pct_incidencia}%)</span>}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setShowCompBreakdown(!showCompBreakdown)}
                    data-testid="toggle-breakdown"
                    className={`ml-auto text-xs px-2 py-1 rounded border transition-colors ${showCompBreakdown ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300'}`}
                  >
                    {showCompBreakdown ? 'Ocultar Detalhes' : 'Ver Detalhes'}
                  </button>
                </div>
                {hasComps && (
                  <p className="text-[10px] text-blue-600 mt-1.5">
                    Exibindo: <strong>{activeLabel}</strong> — valores compostos nos quartis e médias
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Breakdown de Componentes (quando ativado) */}
            {showCompBreakdown && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {COMP_OPTIONS.map(opt => {
                  const s = compStats[opt.key];
                  if (!s) return null;
                  return (
                    <Card key={opt.key} className={`${selectedComps.includes(opt.key) ? 'ring-2 ring-blue-400' : ''}`} data-testid={`comp-card-${opt.key}`}>
                      <CardContent className="py-3 px-4">
                        <p className="text-xs font-semibold text-slate-700">{opt.label}</p>
                        <p className="text-lg font-bold text-slate-900 mt-1">{fmt(s.media)}</p>
                        <div className="text-[10px] text-slate-400 mt-1 space-y-0.5">
                          <p>Incidência: <strong className="text-slate-600">{s.pct_incidencia}%</strong> ({s.n_com_valor}/{s.n_total} registros)</p>
                          <p>1º Q: {fmt(s.p25)} | Mediana: {fmt(s.p50)} | 3º Q: {fmt(s.p75)}</p>
                          <p>Média geral (incl. zeros): {fmt(s.media_geral)}</p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-4 text-white" data-testid="kpi-base">
                <p className="text-[11px] opacity-50 flex items-center gap-1"><DollarSign className="w-3 h-3" /> {hasComps ? activeLabel + ' (Pond.)' : 'Salário Base (Pond.)'}</p>
                <p className="text-xl font-bold mt-1">{fmt(hasComps && compostoGeral ? compostoGeral.media_ponderada : data.salario_base_geral?.media_ponderada)}</p>
                <p className="text-[10px] opacity-40">Média simples: {fmt(hasComps && compostoGeral ? compostoGeral.media : data.salario_base_geral?.media)}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-700 to-blue-600 rounded-xl p-4 text-white" data-testid="kpi-total">
                <p className="text-[11px] opacity-50 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Rem. Total (Pond.)</p>
                <p className="text-xl font-bold mt-1">{fmt(data.remuneracao_total_geral?.media_ponderada)}</p>
                <p className="text-[10px] opacity-40">Mediana: {fmt(data.remuneracao_total_geral?.p50)}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4" data-testid="kpi-freq">
                <p className="text-[11px] text-slate-500 flex items-center gap-1"><Users className="w-3 h-3" /> Frequência Total</p>
                <p className="text-xl font-bold text-slate-900">{data.frequencia_total?.toLocaleString('pt-BR') || 0}</p>
                <p className="text-[10px] text-slate-400">ocupantes mapeados</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4" data-testid="kpi-cargos">
                <p className="text-[11px] text-slate-500 flex items-center gap-1"><BarChart3 className="w-3 h-3" /> Cargos</p>
                <p className="text-xl font-bold text-slate-900">{data.by_role?.length || 0}</p>
                <p className="text-[10px] text-slate-400">{data.n} empresas</p>
              </div>
              {filteredCount > 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="kpi-corte">
                  <p className="text-[11px] text-amber-600 flex items-center gap-1"><Scissors className="w-3 h-3" /> Linha de Corte</p>
                  <p className="text-xl font-bold text-amber-700">{filteredCount}</p>
                  <p className="text-[10px] text-amber-500">cargos com dados filtrados (+/-{linhaCorte}%)</p>
                </div>
              ) : (
                <div className="bg-emerald-50 rounded-xl p-4" data-testid="kpi-corte-ok">
                  <p className="text-[11px] text-emerald-600 flex items-center gap-1"><Scissors className="w-3 h-3" /> Linha de Corte</p>
                  <p className="text-xl font-bold text-emerald-700">OK</p>
                  <p className="text-[10px] text-emerald-500">Nenhum dado filtrado</p>
                </div>
              )}
            </div>

            {/* Chart View */}
            {view === 'chart' && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>{activeLabel} — 1º Quartil / Mediana / 3º Quartil / 9º Decil</span>
                    <span className="text-[10px] text-slate-400 font-normal">Linha = mediana geral ({fmtK(medianGeral)})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={Math.max(380, chartData.length * 42)}>
                    <ComposedChart data={chartData} layout="vertical" margin={{ top: 10, right: 30, bottom: 10, left: 140 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => fmtK(v)} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={135} />
                      <Tooltip content={<CustomTooltip />} />
                      <ReferenceLine x={medianGeral} stroke="#94a3b8" strokeDasharray="6 4" strokeWidth={1.5} />
                      <Bar dataKey="q1" fill="transparent" stackId="box" />
                      <Bar dataKey="iq" stackId="box">{chartData.map((_, i) => <Cell key={i} fill="#2563eb" opacity={0.7} />)}</Bar>
                      <Bar dataKey="upper" stackId="box" radius={[0, 3, 3, 0]}>{chartData.map((_, i) => <Cell key={i} fill="#93c5fd" opacity={0.5} />)}</Bar>
                      <Line type="monotone" dataKey="P50" stroke="#0f172a" strokeWidth={0} dot={{ r: 4, fill: '#0f172a', stroke: '#fff', strokeWidth: 2 }} />
                      <Line type="monotone" dataKey="myVal" stroke="transparent" strokeWidth={0} dot={({ cx, cy, payload }) => payload.myVal > 0 ? (
                        <circle key={`my-${cx}`} cx={cx} cy={cy} r={5} fill="#ef4444" stroke="#fff" strokeWidth={2} />
                      ) : null} />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div className="flex items-center gap-4 mt-2 text-[10px] text-slate-500 px-4 flex-wrap">
                    <span className="flex items-center gap-1"><span className="w-3 h-2.5 rounded-sm bg-blue-500 opacity-70 inline-block" /> 1º-3º Quartil</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-2.5 rounded-sm bg-blue-300 opacity-50 inline-block" /> 3º Quartil-9º Decil</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-900 inline-block" /> Mediana</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Sua Empresa</span>
                    {hasComps && <span className="text-blue-600 font-medium">Valores compostos: {activeLabel}</span>}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Table View */}
            {view === 'table' && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Tabela de Mercado — {activeLabel} (Média Ponderada por Frequência)</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="salary-table">
                      <thead>
                        <tr className="border-b-2 border-slate-200">
                          <th className="py-2.5 px-2 text-left font-semibold text-slate-700" rowSpan={2}>Cargo</th>
                          <th className="py-2.5 px-2 text-center font-semibold text-slate-700" rowSpan={2}>N</th>
                          <th className="py-2.5 px-2 text-center font-semibold text-slate-700" rowSpan={2}>Freq.</th>
                          <th className="py-1 px-2 text-center font-semibold text-blue-700 border-b border-blue-200" colSpan={6}>{activeLabel}</th>
                          {selectedComps.length > 0 && selectedComps.map(k => (
                            <th key={k} className="py-1 px-2 text-center font-semibold text-violet-600 border-b border-violet-200" colSpan={2}>
                              {COMP_OPTIONS.find(c => c.key === k)?.short}
                            </th>
                          ))}
                          <th className="py-1 px-2 text-center font-semibold text-slate-700 border-b border-slate-200" colSpan={2}>Sua Empresa</th>
                        </tr>
                        <tr className="border-b border-slate-200">
                          <th className="py-1.5 px-1.5 text-right text-[10px] text-slate-500">1º Quartil</th>
                          <th className="py-1.5 px-1.5 text-right text-[10px] font-semibold text-blue-600">Mediana</th>
                          <th className="py-1.5 px-1.5 text-right text-[10px] text-slate-500">3º Quartil</th>
                          <th className="py-1.5 px-1.5 text-right text-[10px] text-slate-500">9º Decil</th>
                          <th className="py-1.5 px-1.5 text-right text-[10px] text-slate-500">Média</th>
                          <th className="py-1.5 px-1.5 text-right text-[10px] font-semibold text-slate-600">Pond.</th>
                          {selectedComps.map(k => (
                            <React.Fragment key={k}>
                              <th className="py-1.5 px-1.5 text-right text-[10px] text-violet-500">Média</th>
                              <th className="py-1.5 px-1.5 text-right text-[10px] text-violet-500">Incid.</th>
                            </React.Fragment>
                          ))}
                          <th className="py-1.5 px-1.5 text-right text-[10px] text-slate-600">Valor</th>
                          <th className="py-1.5 px-1.5 text-center text-[10px] text-slate-600">vs Med.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.by_role || []).map((r, i) => {
                          const me = r.minha_empresa;
                          const src = hasComps && r.composto ? r.composto : r.salario_base;
                          const hasFilter = r.items_removidos_corte > 0;
                          const meVal = me ? (hasComps ? me.valor_composto : me.salario_base) : 0;
                          return (
                            <tr key={i} data-testid={`salary-row-${i}`} className={`border-b border-slate-50 transition-colors ${hasFilter ? 'bg-amber-50/30' : i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'} hover:bg-blue-50/30`}>
                              <td className="py-2 px-2">
                                <div className="flex items-center gap-1.5">
                                  {hasFilter && <Scissors className="w-3 h-3 text-amber-500 shrink-0" />}
                                  <span className="font-medium text-slate-900">{r.cargo}</span>
                                </div>
                                <span className="text-[10px] text-slate-400 capitalize">{r.nivel}</span>
                                {hasFilter && <p className="text-[9px] text-amber-600">{r.items_removidos_corte} valor(es) removido(s)</p>}
                              </td>
                              <td className="py-2 px-2 text-center text-slate-500">{r.n_empresas_filtradas || r.n_empresas}</td>
                              <td className="py-2 px-2 text-center font-medium text-slate-700">{r.frequencia}</td>
                              <td className="py-2 px-1.5 text-right text-slate-500">{fmt(src?.p25)}</td>
                              <td className="py-2 px-1.5 text-right font-semibold text-blue-700">{fmt(src?.p50)}</td>
                              <td className="py-2 px-1.5 text-right text-slate-500">{fmt(src?.p75)}</td>
                              <td className="py-2 px-1.5 text-right text-slate-400">{fmt(src?.p90)}</td>
                              <td className="py-2 px-1.5 text-right text-slate-500">{fmt(src?.media)}</td>
                              <td className="py-2 px-1.5 text-right font-semibold text-slate-800">{fmt(src?.media_ponderada)}</td>
                              {selectedComps.map(k => {
                                const cs = r.componentes?.[k];
                                return (
                                  <React.Fragment key={k}>
                                    <td className="py-2 px-1.5 text-right text-violet-600">{cs?.media > 0 ? fmt(cs.media) : '-'}</td>
                                    <td className="py-2 px-1.5 text-right text-violet-400 text-[10px]">{cs?.pct_incidencia > 0 ? `${cs.pct_incidencia}%` : '-'}</td>
                                  </React.Fragment>
                                );
                              })}
                              <td className="py-2 px-1.5 text-right text-slate-800">{me ? fmt(meVal) : '-'}</td>
                              <td className="py-2 px-1.5 text-center">
                                {me ? (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${me.diff_pct > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                    {me.diff_pct > 0 ? '+' : ''}{me.diff_pct}%
                                  </span>
                                ) : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-[10px] text-slate-500 border-t border-slate-100 pt-3 flex-wrap">
                    <span>Pond. = Média Ponderada (por frequência)</span>
                    {hasComps && <span className="text-blue-600">Valores compostos: {activeLabel}</span>}
                    <span className="flex items-center gap-1"><Scissors className="w-3 h-3 text-amber-500" /> Linha de corte: +/-{linhaCorte}%</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default SalaryDashboard;
